// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * SupportDashboardPage
 *
 * Main dashboard view for support staff showing assigned advisors,
 * quick actions, recent activity, and profiles requiring attention.
 */

import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdvisor } from '../contexts/AdvisorContext';
import { Alert, AppIcon, Spinner, Button } from '@loa/ui-components';
import { supportApi } from '../api/supportApi';
import { DashboardActivityFeed } from '../components/dashboard';
import type {
  AssignedAdvisorInfo,
  ProfileWithSupportInfo,
  ActivityLogEntry,
} from '../types/support';

// ============================================
// Types
// ============================================

interface SupportDashboardStats {
  totalAdvisors: number;
  totalProfiles: number;
  pendingLOAs: number;
  actionsThisWeek: number;
}

// ============================================
// Advisor Card Component
// ============================================

type AdvisorCardProps = Readonly<{
  advisor: AssignedAdvisorInfo;
  onViewProfiles: () => void;
}>;

function AdvisorCard({ advisor, onViewProfiles }: AdvisorCardProps) {
  const initials = `${advisor.firstName.charAt(0)}${advisor.lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="fin-card">
      <div className="fin-card__content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '1em' }}>
          {advisor.photoUrl ? (
            <img
              src={advisor.photoUrl}
              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              alt={`${advisor.firstName} ${advisor.lastName}`}
            />
          ) : (
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#2185D0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.1rem',
                fontWeight: 'bold',
              }}
            >
              {initials}
            </div>
          )}
          <div>
            <div style={{ marginBottom: '0.25em', fontWeight: '600' }}>
              {advisor.firstName} {advisor.lastName}
            </div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{advisor.email}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1em', marginBottom: '1em' }}>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>
              {advisor.profileCount}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Profiles</div>
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: advisor.pendingLOACount > 0 ? '#F2711C' : '#333' }}>
              {advisor.pendingLOACount}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Pending LOAs</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '0.75em 1em', borderTop: '1px solid #f0f0f0' }}>
        <Button variant="filled" fluid onClick={onViewProfiles}>
          View Profiles
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Quick Stats Card Component
// ============================================

type QuickStatsProps = Readonly<{
  stats: SupportDashboardStats;
  loading: boolean;
}>;

function QuickStats({ stats, loading }: QuickStatsProps) {
  if (loading) {
    return (
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AppIcon name="chart bar" />
          Quick Stats
        </h4>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1em' }}>
        <AppIcon name="chart bar" />
        Quick Stats
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', borderTop: '1px solid #e0e0e0', paddingTop: '1em' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#2185D0' }}>
            {stats.totalAdvisors}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Assigned Advisors</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#00B5AD' }}>
            {stats.totalProfiles}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Profiles</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: stats.pendingLOAs > 0 ? '#F2711C' : '#333' }}>
            {stats.pendingLOAs}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Pending LOAs</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#21BA45' }}>
            {stats.actionsThisWeek}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Actions This Week</div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Quick Actions Component
// ============================================

type QuickActionsProps = Readonly<{
  onCreateProfile: () => void;
  disabled: boolean;
}>;

function QuickActions({ onCreateProfile, disabled }: QuickActionsProps) {
  return (
    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1em' }}>
        <AppIcon name="lightning" />
        Quick Actions
      </h4>
      <Button
        variant="filled"
        fluid
        onClick={onCreateProfile}
        disabled={disabled}
        style={{ marginBottom: '0.5em' }}
      >
        <AppIcon name="plus" />
        Create New Profile
      </Button>
    </div>
  );
}

// ============================================
// Profiles Requiring Attention Component
// ============================================

type ProfilesRequiringAttentionProps = Readonly<{
  profiles: ProfileWithSupportInfo[];
  loading: boolean;
  onViewProfile: (profileId: string) => void;
  onViewAll: () => void;
}>;

function ProfilesRequiringAttention({
  profiles,
  loading,
  onViewProfile,
  onViewAll,
}: ProfilesRequiringAttentionProps) {
  if (loading) {
    return (
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
          <h4 style={{ margin: 0 }}>
            <AppIcon name="attention" color="orange" />
            Profiles Requiring Attention
          </h4>
        </div>
        <Spinner />
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
          <h4 style={{ margin: 0 }}>
            <AppIcon name="attention" color="orange" />
            Profiles Requiring Attention
          </h4>
        </div>
        <Alert variant="success">
          <AppIcon name="check circle" />
          All profiles are up to date. No action required.
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
        <h4 style={{ margin: 0 }}>
          <AppIcon name="attention" color="orange" />
          Profiles Requiring Attention ({profiles.length})
        </h4>
        <Button variant="outlined" size="sm" onClick={onViewAll}>
          View All
        </Button>
      </div>
      {profiles.slice(0, 5).map((profile) => (
        <button
          type="button"
          key={profile.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75em 1em',
            marginBottom: '0.5em',
            cursor: 'pointer',
            background: '#f9f9f9',
            borderRadius: '4px',
            border: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            width: '100%',
          }}
          onClick={() => onViewProfile(profile.id)}
        >
          <div>
            <div style={{ fontWeight: 'bold' }}>{profile.profileName}</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>
              {profile.assignedToName} &middot; {profile.pendingLOACount} pending LOA{profile.pendingLOACount !== 1 ? 's' : ''}
            </div>
          </div>
          <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '0.85em', background: '#f2711c', color: 'white' }}>
            Needs Attention
          </span>
        </button>
      ))}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function SupportDashboardPage() {
  const navigate = useNavigate();
  const { advisor, loading: advisorLoading, error: advisorError } = useAdvisor();

  // State
  const [assignedAdvisors, setAssignedAdvisors] = useState<AssignedAdvisorInfo[]>([]);
  const [stats, setStats] = useState<SupportDashboardStats>({
    totalAdvisors: 0,
    totalProfiles: 0,
    pendingLOAs: 0,
    actionsThisWeek: 0,
  });
  const [recentActivity, setRecentActivity] = useState<ActivityLogEntry[]>([]);
  const [profilesRequiringAttention, setProfilesRequiringAttention] = useState<ProfileWithSupportInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================
  // Data Loading
  // ============================================

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all dashboard data in parallel
        const [advisorsResponse, statsResponse, activityResponse, attentionResponse] = await Promise.all([
          supportApi.getMyAssignedAdvisors(),
          supportApi.getSupportDashboardStats(),
          supportApi.getSupportRecentActivity(10),
          supportApi.getProfilesRequiringAttention(),
        ]);

        setAssignedAdvisors(advisorsResponse);
        setStats(statsResponse);
        setRecentActivity(activityResponse);
        setProfilesRequiringAttention(attentionResponse);
      } catch (err) {
        console.error('Failed to load support dashboard:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // ============================================
  // Event Handlers
  // ============================================

  const handleViewAdvisorProfiles = useCallback((advisorId: string) => {
    navigate(`/relationships?advisorId=${advisorId}`);
  }, [navigate]);

  const handleCreateProfile = useCallback(() => {
    navigate('/relationships/new');
  }, [navigate]);

  const handleViewProfile = useCallback((profileId: string) => {
    navigate(`/relationships/${profileId}`);
  }, [navigate]);

  const handleViewAllAttention = useCallback(() => {
    navigate('/relationships?status=needs-attention');
  }, [navigate]);

  const handleViewAllActivity = useCallback(() => {
    navigate('/activity-log');
  }, [navigate]);

  // ============================================
  // Loading and Error States
  // ============================================

  if (advisorLoading) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Spinner />
      </div>
    );
  }

  if (advisorError) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="error" header="Error Loading Account">
          <p>{advisorError}</p>
        </Alert>
      </div>
    );
  }

  if (!advisor) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="warning" header="No Account Found">
          <p>Please contact your administrator.</p>
        </Alert>
      </div>
    );
  }

  // ============================================
  // Dashboard Layout
  // ============================================

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5em' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25em' }}>
          <AppIcon name="headphones" color="teal" />
          Support Dashboard
        </h2>
        <p style={{ color: '#666', margin: 0 }}>
          Hello, {advisor.firstName}. You are assisting {assignedAdvisors.length} advisor{assignedAdvisors.length !== 1 ? 's' : ''}.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="error" header="Error Loading Dashboard" style={{ marginBottom: '1.5em' }}>
          <p>{error}</p>
        </Alert>
      )}

      {/* My Assigned Advisors Section */}
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <h3 style={{ marginBottom: '1em' }}>
          <AppIcon name="users" />
          My Assigned Advisors
        </h3>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1em' }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ padding: '1em', background: '#f9f9f9', borderRadius: '4px' }}>
                <Spinner />
              </div>
            ))}
          </div>
        ) : assignedAdvisors.length === 0 ? (
          <Alert variant="info" header="No Advisors Assigned">
            <p>Contact your administrator to get assigned to advisors.</p>
          </Alert>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1em' }}>
            {assignedAdvisors.map((advisorInfo) => (
              <AdvisorCard
                key={advisorInfo.id}
                advisor={advisorInfo}
                onViewProfiles={() => handleViewAdvisorProfiles(advisorInfo.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'flex', gap: '1.5em', marginTop: '1.5em', flexWrap: 'wrap' }}>
        {/* Left Column: Quick Actions + Quick Stats */}
        <div style={{ flex: '0 0 35%', minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <QuickActions
            onCreateProfile={handleCreateProfile}
            disabled={assignedAdvisors.length === 0}
          />
          <QuickStats stats={stats} loading={loading} />
        </div>

        {/* Right Column: Recent Activity */}
        <div style={{ flex: '1 1 300px' }}>
          <DashboardActivityFeed
            activities={recentActivity.map((entry) => ({
              id: entry.id,
              type: entry.type as unknown as import('../types/dashboard').ActivityType,
              title: entry.title,
              subtitle: entry.subtitle,
              timestamp: entry.timestamp,
              resourceType: entry.resourceType,
              resourceId: entry.resourceId,
              profileId: entry.profileId,
              icon: entry.type.toLowerCase().replace('_', '-'),
            }))}
            maxDisplay={10}
            loading={loading}
            onActivityClick={() => {}}
            onViewAll={handleViewAllActivity}
          />
        </div>
      </div>

      {/* Profiles Requiring Attention */}
      <div style={{ marginTop: '1.5em' }}>
        <ProfilesRequiringAttention
          profiles={profilesRequiringAttention}
          loading={loading}
          onViewProfile={handleViewProfile}
          onViewAll={handleViewAllAttention}
        />
      </div>
    </div>
  );
}

export default SupportDashboardPage;
